import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useLayout } from "../../../providers/LayoutProvider";

import { useTalesRepo } from "../db/useTalesRepo";
import { getMidnightInUtc } from "../../../utils/dateUtils";

// component
import HomeFooter from "../components/HomeFooter";

// icons
import crImg from "../assets/images/cr.jpeg";
import flImg from "../assets/images/fl.jpeg";
import TaleCard from "../components/TaleCard";

/**
 * HomePage
 * - Renders banner + create-tale form + next tale preview.
 * - Uses TaleCard for the next-tale preview so you can reuse the card elsewhere.
 *
 */
export default function HomePage({
  headerImageSrc = crImg,
  onCreateTale, // do not default to a string; leave as undefined if not provided
}) {
  // use setters from LayoutProvider to set display and/or set header fooder content
  const { setHeaderContent, setFooterContent } = useLayout();
  const { setShowHeader, setShowFooter } = useLayout();

  const navigate = useNavigate();
  const repo = useTalesRepo();

  const [destination, setDestination] = useState("");
  const [activeTale, setActiveTale] = useState(null);
  const inputRef = useRef(null);

  // set doc title
  useEffect(() => {
    document.title = "TOAT - Home";
  }, []);

  // Mount/unmount header + footer
  useEffect(() => {
    // hide header on this page
    setShowHeader(true);
    // show footer on this page
    setShowFooter(true);

    // Optionally set custom content instead:
    setHeaderContent(
      <div className="header-image-container" aria-hidden={!headerImageSrc}>
        {headerImageSrc && (
          <img
            className="header-image"
            src={headerImageSrc}
            alt=""
            loading="eager"
          />
        )}
      </div>
    );
    setFooterContent(<HomeFooter />);

    // cleanup runs on unmount, and on dependency changes
    return () => {
      setShowHeader(true);
      setShowFooter(true);
      setHeaderContent(null);
      setFooterContent(null);
    };
  }, [setShowHeader, setShowFooter, setHeaderContent, setFooterContent]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // get all settings
  useEffect(() => {
    (async () => {
      try {
        const tales = await repo.getAll();

        // Filter tales that haven't ended yet and sort by start date
        const futureTales = tales
          .filter((tale) => {
            const taleStartDate = getMidnightInUtc(
              tale.startDateTime
            ).midnightTimestamp;
            const taleEndDate = getMidnightInUtc(
              tale.endDateTime
            ).midnightTimestamp;
            // Log the values for comparison
            // if (taleStartDate > getMidnightInUtc().midnightTimestamp)
            //   console.debug(tale.title + " is in future");
            // if (taleStartDate < getMidnightInUtc().midnightTimestamp)
            //   console.debug(tale.title + " is in the past");
            // if (taleStartDate === getMidnightInUtc().midnightTimestamp)
            //   console.debug(tale.title + " is happening");
            // console.debug(
            //   "Tale midnight ts:",
            //   taleStartDate,
            //   "Midnight ts:",
            //   getMidnightInUtc().midnightTimestamp
            // );
            return (
              taleStartDate >= getMidnightInUtc().midnightTimestamp ||
              taleEndDate > getMidnightInUtc().midnightTimestamp
            );
          })
          .sort(
            (a, b) =>
              getMidnightInUtc(b.startDateTime).midnightTimestamp -
              getMidnightInUtc(b.startDateTime).midnightTimestamp
          );

        // const futureTales = tales
        //   .filter(
        //     (tale) =>
        //       new Date(tale.startDateTime).valueOf() >=
        //       getMidnightInUtc().midnightTimestamp
        //   )
        //   .sort(
        //     (a, b) =>
        //       new Date(b.startDateTime).valueOf() -
        //       new Date(b.startDateTime).valueOf()
        //   );

        if (futureTales.length > 0) setActiveTale(futureTales[0]);
        //either currently happening or upcoming
        else {
          const pastTales = tales
            .filter(
              (tale) =>
                getMidnightInUtc(tale.endDateTime).midnightTimestamp <
                getMidnightInUtc().midnightTimestamp
            )
            .sort(
              (a, b) =>
                getMidnightInUtc(a.endDateTime).midnightTimestamp -
                getMidnightInUtc(b.endDateTime).midnightTimestamp
            );

          if (pastTales.length > 0) setActiveTale(pastTales[0]);
        }
        // console.debug(flImg);
        // activeTale.image =
        //   "https://localhost:3000/static/media/fl.17491d4e3f90eeb4a36e.jpeg";
      } catch (e) {
        // setError(e?.message || "Failed to load next Tale");
        // console.error("Failed to fetch Tale:", error);
      }
    })();
  }, []); // run once

  // handle adding new tale
  function handleSubmit(e) {
    e.preventDefault();
    const title = destination.trim();
    navigate(`/tale/add/${title}`);
    // if (!title) return inputRef.current?.focus();
    // onCreateTale?.(title);
    // alert("Create: " + title);
    // setDestination("");
  }

  // TaleCard activation → navigate to tale details (id preferred, else slugified name)
  // function onOpenTale(tale) {
  //   const slug =
  //     tale?.id ??
  //     encodeURIComponent(
  //       String(tale?.name || "")
  //         .toLowerCase()
  //         .replace(/\s+/g, "-")
  //     );
  //   navigate(`/tale/add/${slug}`);
  // }

  return (
    <>
      <div className="content-container">
        {/* Create Tale form */}
        <div className="home-title" aria-labelledby="title-label">
          <h1>Tales of a Trip</h1>
        </div>
        <div
          className="input-card"
          role="form"
          aria-labelledby="destination-label"
        >
          <form onSubmit={handleSubmit}>
            <label id="destination-label" htmlFor="destination-input">
              Your next tale starts here...
            </label>
            <input
              ref={inputRef}
              type="text"
              id="destination-input"
              name="destination"
              inputMode="text"
              autoComplete="off"
              placeholder="What should we call it?" //"Title of your next tale"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              aria-describedby="destination-help"
            />
            <p id="destination-help" className="sr-only">
              {/* What should we call this new adventure? */}
            </p>
            <button
              className="submit-button"
              type="submit"
              aria-label="Create new tale"
            >
              Start now!
            </button>
          </form>
        </div>

        {/* Next tale preview (TaleCard replaces the inline card markup) */}

        {activeTale && (
          <section
            className="landing-tale-container"
            aria-label="Your next tale"
          >
            {/* {activeTale &&
            getMidnightInUtc(activeTale.startDateTime).midnightTimestamp >
              getMidnightInUtc().midnightTimestamp ? (
              <label className="tale-preview-label" htmlFor="tale-card">
                Your upcoming tale…
              </label>
            ) : activeTale &&
              getMidnightInUtc(activeTale.startDateTime).midnightTimestamp <=
                getMidnightInUtc().midnightTimestamp &&
              getMidnightInUtc(activeTale.endDateTime).midnightTimestamp >=
                getMidnightInUtc().midnightTimestamp ? (
              <label className="tale-preview-label" htmlFor="tale-card">
                Hope you are having fun in...
              </label>
            ) : activeTale &&
              getMidnightInUtc(activeTale.endDateTime).midnightTimestamp <
                getMidnightInUtc().midnightTimestamp ? (
              <label className="tale-preview-label" htmlFor="tale-card">
                Oh No!
              </label>
            ) : (
              <div>Oops</div>
            )} */}
            <TaleCard key={1} tale={activeTale} />
          </section>
        )}
      </div>
    </>
  );
}
